import React, { useState, useEffect, useContext, useCallback } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import Typography from "@material-ui/core/Typography";
import Collapse from "@material-ui/core/Collapse";
import Link from "@material-ui/core/Link";

import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import SyncIcon from "@material-ui/icons/Sync";
import Tooltip from "@material-ui/core/Tooltip";

import TableRowSkeleton from "../TableRowSkeleton";
import ConfirmationModal from "../ConfirmationModal";
import DefaultPluginModal from "../DefaultPluginModal";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  url: {
    fontFamily: "monospace",
    fontSize: "0.8rem",
    wordBreak: "break-all",
  },
  celulaEmpresas: {
    paddingTop: 0,
    paddingBottom: 0,
    borderBottom: "none",
  },
  empresas: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 0, 2),
  },
  cabecalho: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 0, 2),
  },
  explicacao: {
    flex: 1,
    color: theme.palette.text.secondary,
  },
  semAcesso: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

const DefaultPluginsPanel = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [emEdicao, setEmEdicao] = useState(null);
  const [confirmando, setConfirmando] = useState(null);
  // Ação em massa fica em estado PRÓPRIO: reusar o de remover faria um clique
  // em "desinstalar de todas" abrir o texto de "remover dos padrões".
  const [emMassa, setEmMassa] = useState(null);
  const [executando, setExecutando] = useState(false);
  // Qual linha está com a lista de empresas aberta (só uma por vez).
  const [empresasAbertas, setEmpresasAbertas] = useState(null);

  // Conveniência de UI: quem autoriza de verdade é o isMasterAdmin no servidor.
  const podeAdministrar = !!user?.masterAdmin;

  const carregar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master/default-plugins");
      setPlugins(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!podeAdministrar) {
      setLoading(false);
      return;
    }
    carregar();
  }, [podeAdministrar, carregar]);

  const executarEmMassa = async () => {
    if (!emMassa) return;
    setExecutando(true);
    try {
      const { data } = await api.post(
        `/master/default-plugins/${emMassa.plugin.id}/${emMassa.acao}`
      );
      toast.success(
        i18n.t("defaultPlugins.emMassa.resultado", {
          afetadas: data.afetadas,
          falhas: data.falhas,
        })
      );
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setExecutando(false);
      setEmMassa(null);
    }
  };

  const abrirNovo = () => {
    setEmEdicao(null);
    setModalAberto(true);
  };

  const abrirEdicao = (plugin) => {
    setEmEdicao(plugin);
    setModalAberto(true);
  };

  const fecharModal = () => {
    setModalAberto(false);
    setEmEdicao(null);
    carregar();
  };

  const remover = async (plugin) => {
    try {
      await api.delete(`/master/default-plugins/${plugin.id}`);
      toast.success(i18n.t("defaultPlugins.removido"));
      carregar();
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmando(null);
    }
  };

  if (!podeAdministrar) {
    return (
      <Paper className={classes.mainPaper} variant="outlined">
        <Typography className={classes.semAcesso}>
          {i18n.t("defaultPlugins.semAcesso")}
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <ConfirmationModal
        title={
          confirmando
            ? `${i18n.t("defaultPlugins.confirmarRemocao.titulo")} ${confirmando.name || confirmando.baseUrlTemplate}?`
            : ""
        }
        open={!!confirmando}
        onClose={() => setConfirmando(null)}
        onConfirm={() => remover(confirmando)}
      >
        {i18n.t("defaultPlugins.confirmarRemocao.mensagem")}
      </ConfirmationModal>

      <ConfirmationModal
        title={
          emMassa
            ? i18n.t(`defaultPlugins.emMassa.${emMassa.acao}.titulo`)
            : ""
        }
        open={!!emMassa && !executando}
        onClose={() => setEmMassa(null)}
        onConfirm={executarEmMassa}
      >
        {emMassa &&
          i18n.t(`defaultPlugins.emMassa.${emMassa.acao}.mensagem`, {
            empresas: emMassa.plugin.instaladoEm,
          })}
      </ConfirmationModal>

      <DefaultPluginModal
        open={modalAberto}
        onClose={fecharModal}
        plugin={emEdicao}
      />

      <div className={classes.cabecalho}>
        <Typography variant="body2" className={classes.explicacao}>
          {i18n.t("defaultPlugins.explicacao")}
        </Typography>
        <Button variant="contained" color="primary" onClick={abrirNovo}>
          {i18n.t("defaultPlugins.botoes.adicionar")}
        </Button>
      </div>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("defaultPlugins.campos.baseUrlTemplate")}</TableCell>
              <TableCell>{i18n.t("defaultPlugins.campos.plugin")}</TableCell>
              <TableCell align="center">
                {i18n.t("defaultPlugins.campos.active")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("defaultPlugins.campos.instaladoEm")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("defaultPlugins.campos.acoes")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={5} />
            ) : (
              <>
                {plugins.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {i18n.t("defaultPlugins.vazio")}
                    </TableCell>
                  </TableRow>
                )}
                {plugins.map((plugin) => [
                  <TableRow key={plugin.id}>
                    <TableCell className={classes.url}>
                      {plugin.baseUrlTemplate}
                    </TableCell>
                    <TableCell>
                      {plugin.descoberto ? (
                        <>
                          {plugin.name || plugin.pluginId}
                          <Typography
                            variant="caption"
                            display="block"
                            color="textSecondary"
                          >
                            {plugin.pluginId}
                          </Typography>
                        </>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {i18n.t("defaultPlugins.descoberto.pendente")}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={
                          plugin.active
                            ? i18n.t("defaultPlugins.status.ativo")
                            : i18n.t("defaultPlugins.status.inativo")
                        }
                        color={plugin.active ? "primary" : "default"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {plugin.instaladoEm > 0 ? (
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() =>
                            setEmpresasAbertas(
                              empresasAbertas === plugin.id ? null : plugin.id
                            )
                          }
                        >
                          {plugin.instaladoEm}
                        </Link>
                      ) : (
                        0
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" onClick={() => abrirEdicao(plugin)}>
                        <EditIcon />
                      </IconButton>
                      <Tooltip title={i18n.t("defaultPlugins.emMassa.reapontar.titulo")}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!plugin.instaladoEm}
                            onClick={() =>
                              setEmMassa({ plugin, acao: "reapontar" })
                            }
                          >
                            <SyncIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={i18n.t("defaultPlugins.emMassa.desinstalar.titulo")}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={!plugin.instaladoEm}
                            onClick={() =>
                              setEmMassa({ plugin, acao: "desinstalar" })
                            }
                          >
                            <LinkOffIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <IconButton
                        size="small"
                        onClick={() => setConfirmando(plugin)}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>,
                  <TableRow key={`${plugin.id}-empresas`}>
                    <TableCell
                      colSpan={5}
                      className={classes.celulaEmpresas}
                    >
                      <Collapse
                        in={empresasAbertas === plugin.id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <div className={classes.empresas}>
                          {plugin.empresas?.map((e) => (
                            <Chip
                              key={e.id}
                              size="small"
                              label={`${e.id} — ${e.name}`}
                              variant="outlined"
                            />
                          ))}
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>,
                ])}
              </>
            )}
          </TableBody>
        </Table>
      </Paper>
    </>
  );
};

export default DefaultPluginsPanel;
